import { create } from 'zustand';
import { looksLikeSqlError } from '@/shared/lib/octo/sqlError';
import { useErrorDialogStore } from './errorDialogStore';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';
interface ToastItem {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
  /** Tıklanınca çalışır (ör. gelen mesajın konuşmasını açmak); toast kapanır. */
  onClick?: () => void;
  /** Bildirimin içinde ayrı bir düğme (ör. "Ayrıntı"); tıklanınca toast kapanır. */
  action?: { label: string; onClick: () => void };
  /** Aynı bildirim üst üste geldiyse kaç kez (#504: "Kayıt silindi ×3"). */
  count?: number;
}
interface ToastState {
  items: ToastItem[];
  push: (t: Omit<ToastItem, 'id'>) => void;
  remove: (id: number) => void;
}
let seq = 1;
export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (t) =>
    set((s) => {
      // Aynı metin ve tonda, tıklanmayan bir bildirim zaten ekrandaysa yenisi eklenmez: sayaç
      // artar ve süresi baştan başlar (yeni id) — toplu işlemde ekran bildirimle dolmasın (#504).
      if (!t.onClick && !t.action) {
        const same = s.items.find(
          (i) => !i.onClick && !i.action && i.tone === t.tone && i.title === t.title && i.description === t.description,
        );
        if (same)
          return {
            items: s.items.map((i) => (i === same ? { ...i, id: seq++, count: (i.count ?? 1) + 1 } : i)),
          };
      }
      return { items: [...s.items, { ...t, id: seq++ }] };
    }),
  remove: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
}));

export const toast = {
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'info' } as ToastItem),
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'success' } as ToastItem),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'warning' } as ToastItem),
  /** İçinde düğme olan bildirim (ör. kısmi başarıda "Ayrıntı"). */
  withAction: (tone: ToastTone, title: string, action: { label: string; onClick: () => void }, description?: string) =>
    useToastStore.getState().push({ title, description, tone, action } as ToastItem),
  /** Tıklanabilir bilgi bildirimi (gelen mesaj / bildirim). */
  action: (title: string, description: string | undefined, onClick: () => void) =>
    useToastStore.getState().push({ title, description, tone: 'info', onClick } as ToastItem),
  /**
   * Hata bildirimi.
   *
   * Metin veritabanından gelen `Konum / Satır / Mesaj` kalıbındaysa bildirim yerine
   * pencere açılır: o blok bildirime sığmıyor ve iş mesajı teknik gürültünün içinde
   * kayboluyordu. Çağıran tarafın bunu bilmesi gerekmiyor — programın her yerindeki
   * `toast.error` çağrıları olduğu gibi kalıyor.
   */
  error: (title: string, description?: string) => {
    if (looksLikeSqlError(title)) {
      useErrorDialogStore.getState().show(title);
      return;
    }
    useToastStore.getState().push({ title, description, tone: 'danger' } as ToastItem);
  },
};
